import React, { useState, useRef } from 'react';
import {
  Typography, Button, TextField, InputAdornment, Alert
} from '@mui/material';
import HeaderCandidat from '../header/headerCand';
import Footer from '../commun/Footer';
import DisponibilitePicker from '../commun/DisponibilitePicker';
import FormInputMUI from '../commun/FormInputMUI';
import { CurrencySelect } from '../Recruteur/candidat/AddTalent/CurrencySelect';
import { contractTypeOptions } from '../commun/constants/options';
import './MesCvs.css';
import axios from 'axios';
import useMessages from '../../hooks/useMessages';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const MesCvs = () => {
  const [formData, setFormData] = useState({
    cv: null,
    disponibilite: '',
    typesContrat: [],
    remunerations: {},
    devises: {}
  });

  const fileInputRef = useRef(null);
  const { messages, handleSuccess, handleError } = useMessages();
  const [errors, setErrors] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setField = (name, value) =>
    setFormData(prev => ({ ...prev, [name]: value }));

  const validate = () => {
    const newErrors = {};
    if (!formData.cv) newErrors.cv = 'CV requis.';
    if (!formData.disponibilite) newErrors.disponibilite = 'Champ requis.';
    if (!formData.typesContrat.length) newErrors.typesContrat = 'Au moins un type de contrat.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = e => {
    if (e.target.files[0]) setField('cv', e.target.files[0]);
  };

  const handleContractTypeChange = (selectedTypes) => {
    const selectedArray = Array.isArray(selectedTypes) ? selectedTypes : [];
    setFormData(prev => ({
      ...prev,
      typesContrat: selectedArray,
      remunerations: Object.fromEntries(selectedArray.map(({ value }) => [value, prev.remunerations[value] || ''])),
      devises: Object.fromEntries(selectedArray.map(({ value }) => [value, prev.devises[value] || '€']))
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    const data = new FormData();
    for (const [key, value] of Object.entries(formData)) {
      if (key === 'remunerations' || key === 'devises') {
        data.append(key, JSON.stringify(value));
      } else if (key === 'typesContrat') {
        value.forEach(val => data.append(`${key}[]`, val.value));
      } else {
        data.append(key, value);
      }
    }

    try {
      const userId = localStorage.getItem("userId");
      await axios.post(`${API_BASE}/api/candidat/uploadcv/${userId}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      handleSuccess("CV enregistré avec succès.");
      setFormData({ cv: null, disponibilite: '', typesContrat: [], remunerations: {}, devises: {} });
      setShowForm(false);
      fileInputRef.current.value = null;
    } catch (err) {
      handleError(err.response?.data?.message || "Erreur lors de l’envoi.");
    } finally {
      setIsSubmitting(false);
    }
  };

  
  return (

    <>
          <HeaderCandidat />
    <div className="mes-cvs-container">
      <div className="mes-cvs-left">
        <Typography variant="h4" gutterBottom>Mes CVs</Typography>
        <Typography variant="body1" className="cvs-description">
          Gardez votre profil à jour et maximisez vos chances d’être contacté.
        </Typography>
        {!showForm && (
          <Button variant="contained" onClick={() => setShowForm(true)} className="deposer-btn">
            Déposer mon CV
          </Button>
        )}

        {showForm && (
          <form onSubmit={handleSubmit} className="mes-cvs-form">
            {messages.error && <Alert severity="error">{messages.error}</Alert>}
            {messages.success && <Alert severity="success">{messages.success}</Alert>}

            <DisponibilitePicker
              label="Disponibilité"
              required
              value={formData.disponibilite}
              onChange={val => setField('disponibilite', val)}
              error={!!errors.disponibilite}
              helperText={errors.disponibilite}
            />

            <FormInputMUI
              type="chip-select"
              label="Types de contrat"
              required
              value={formData.typesContrat}
              options={contractTypeOptions}
              onChange={handleContractTypeChange}
              name="typesContrat"
              containerClass="select-fullwidth"
              error={errors.typesContrat}
            />

            {formData.typesContrat.map(({ value }) => (
              <TextField
                key={value}
                label={`Rémunération pour ${value}`}
                fullWidth
                type="number"
                value={formData.remunerations[value] || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d*$/.test(val)) {
                    setFormData(prev => ({
                      ...prev,
                      remunerations: { ...prev.remunerations, [value]: val }
                    }));
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <CurrencySelect
                        value={formData.devises[value] || '€'}
                        onChange={(currency) => {
                          setFormData(prev => ({
                            ...prev,
                            devises: { ...prev.devises, [value]: currency }
                          }));
                        }}
                      />
                    </InputAdornment>
                  ),
                  inputProps: { min: 0 }
                }}
              />
            ))}

            <Typography variant="body2" className="cv-label">CV *</Typography>
            <Button variant="outlined" onClick={() => fileInputRef.current.click()} className="cv-button">
              {formData.cv ? formData.cv.name : 'Téléverser un CV'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx"
              hidden
              onChange={handleFileChange}
            />
            {errors.cv && (
              <Typography color="error" variant="caption">{errors.cv}</Typography>
            )}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isSubmitting}
              className="submit-cv"
            >
              {isSubmitting ? 'Envoi en cours...' : 'Enregistrer'}
            </Button>
          </form>
        )}
      </div>

      <div className="mes-cvs-right">
        <Typography variant="subtitle1" className="profil-title">
          🗂️ Complétez votre profil
        </Typography>
        <Typography variant="body2" className="profil-desc">
          La majorité des recruteurs IT recherchent directement dans la Cvthèque, sans publier d’offres. Soyez visible !
        </Typography>
        <Button variant="contained" onClick={() => setShowForm(true)} fullWidth>
          Déposer mon CV
        </Button>
      </div>
     
    </div>
     <Footer />
    </>
    
  );
};

export default MesCvs;
